import { FC, ReactElement, useCallback } from 'react';
import { cva, VariantProps } from 'class-variance-authority';
import { TeamShirt, TeamShirtProps } from '../TeamShirt';
import { twMerge } from 'tailwind-merge';
import {
  AddPlayer,
  ArrowLeft,
  Assist,
  Captain,
  Goal,
  GoalBonus,
  Injured,
  PenaltyMiss,
  PenaltySave,
  RedTicket,
  Reset,
  Transfer,
  TransferInOut,
  ViceCaptain,
  YellowTicket,
} from '../svgs';
import { Typography } from '../Typography';
import substituteIcon from './assets/substitute-icon.png';

const SUBBED_IN_BADGE_ICON_URL =
  'data:image/svg+xml,%3Csvg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"%3E%3Ccircle cx="10" cy="10" r="10" fill="%23EEC880"/%3E%3Cpath d="M10 4.2L14.5 5.85V9.45C14.5 12.45 12.62 14.56 10 15.8C7.38 14.56 5.5 12.45 5.5 9.45V5.85L10 4.2Z" stroke="%23222526" stroke-width="1.2" stroke-linejoin="round"/%3E%3Ctext x="10" y="11.7" text-anchor="middle" font-family="Arial, sans-serif" font-size="7" font-weight="800" fill="%23222526"%3ES%3C/text%3E%3C/svg%3E';

const TeamPlayerCVA = cva([''], {
  variants: {
    mode: {
      add: '',
      remove: '',
      select: '',
      substitute: '',
      transfer: '',
      transferOut: '',
      transferIn: '',
      disabled: '',
      // ? reset: transferIn or transferOut will trigger reset
      // reset:''
    },
    role: {
      captain: '',
      viceCaptain: '',
      pendingCaptain: '',
      pendingViceCaptain: '',
      maxCaptain: '',
    },
    status: {
      injured: '',
      suspended: '',
      unavailable: '',
    },
  },
});

const TRANSFER_ROLES: Array<NonNullable<VariantProps<typeof TeamPlayerCVA>['role']>> = [
  'captain',
  'viceCaptain',
  'pendingCaptain',
  'pendingViceCaptain',
  'maxCaptain',
];

const TeamPlayerNameCVA = cva('py-[--padding-2xs] px-[--padding-xs] rounded-t-[--corner-xs]', {
  variants: {
    mode: {
      add: 'bg-[--teamplayer-add-name-bg] text-[--teamplayer-add-name-text]',
      remove: 'bg-[--teamplayer-remove-name-bg] text-[--teamplayer-remove-name-text]',
      select: 'bg-[--teamplayer-remove-name-bg] text-[--teamplayer-remove-name-text]',
      substitute: 'bg-[--teamplayer-substitute-name-bg] text-[--teamplayer-substitute-name-text]',
      transfer: 'bg-[--teamplayer-transfer-name-bg] text-[--teamplayer-transfer-name-text]',
      transferOut:
        'bg-[--teamplayer-transferOut-name-bg] text-[--teamplayer-transferOut-name-text]',
      transferIn: 'bg-[--teamplayer-transferIn-name-bg] text-[--teamplayer-transferIn-name-text]',
      disabled: 'bg-[--teamplayer-disabled-name-bg] text-[--teamplayer-disabled-name-text]',
    },
    role: {
      captain: 'bg-[--teamplayer-captain-name-bg] text-[--teamplayer-captain-name-text]',
      pendingCaptain: 'border border-dashed border-[#F8CC46] bg-[#FFF6D8] text-[#7A5700]',
      maxCaptain: 'bg-[#F8CC46] text-[#231F20]',
      pendingViceCaptain: 'border border-dashed border-[#E4D8FF] bg-[#F7F1FF] text-[#5A3DA6]',
      viceCaptain:
        'bg-[--teamplayer-vicecaptain-name-bg] text-[--teamplayer-vicecaptain-name-text]',
    },
    status: {
      injured: 'bg-[--teamplayer-transferOut-name-bg] text-[--teamplayer-transferOut-name-text]',
      suspended: '',
      unavailable: '',
    },
  },
  // In/out transfer states must show their green/red bg regardless of role —
  // otherwise a captain/vice-captain's role bg (applied after mode's in CVA's
  // class order) wins the tailwind-merge conflict and masks the transfer color.
  // Injured status must also show remove color regardless of role.
  compoundVariants: [
    {
      mode: 'transferIn',
      role: TRANSFER_ROLES,
      className:
        'border-none bg-[--teamplayer-transferIn-name-bg] text-[--teamplayer-transferIn-name-text]',
    },
    {
      mode: 'transferOut',
      role: TRANSFER_ROLES,
      className:
        'border-none bg-[--teamplayer-transferOut-name-bg] text-[--teamplayer-transferOut-name-text]',
    },
    {
      status: 'injured',
      role: TRANSFER_ROLES,
      className:
        'border-none bg-[--teamplayer-transferOut-name-bg] text-[--teamplayer-transferOut-name-text]',
    },
  ],
});

const TeamPlayerDescCVA = cva('py-[--padding-2xs] px-[--padding-xs] rounded-b-[--corner-xs]', {
  variants: {
    mode: {
      add: 'bg-[--teamplayer-add-desc-bg] text-[--teamplayer-add-desc-text]',
      remove: 'bg-[--teamplayer-remove-desc-bg] text-[--teamplayer-remove-desc-text]',
      select: 'bg-[--teamplayer-remove-desc-bg] text-[--teamplayer-remove-desc-text]',
      substitute: 'bg-[--teamplayer-substitute-desc-bg] text-[--teamplayer-substitute-desc-text]',
      transfer: 'bg-[--teamplayer-transfer-desc-bg] text-[--teamplayer-transfer-desc-text]',
      transferOut:
        'bg-[--teamplayer-transferOut-desc-bg] text-[--teamplayer-transferOut-desc-text]',
      transferIn: 'bg-[--teamplayer-transferIn-desc-bg] text-[--teamplayer-transferIn-desc-text]',
      disabled: 'bg-[--teamplayer-disabled-desc-bg] text-[--teamplayer-disabled-desc-text]',
    },
    role: {
      captain: 'bg-[--teamplayer-captain-desc-bg] text-[--teamplayer-captain-desc-text]',
      pendingCaptain:
        'border-x border-b border-dashed border-[#F8CC46] bg-[#FFFBEA] text-[#7A5700]',
      maxCaptain: 'bg-[#FFF3BF] text-[#231F20]',
      pendingViceCaptain:
        'border-x border-b border-dashed border-[#E4D8FF] bg-[#FCFAFF] text-[#5A3DA6]',
      viceCaptain:
        'bg-[--teamplayer-vicecaptain-desc-bg] text-[--teamplayer-vicecaptain-desc-text]',
    },
  },
  // Same override as TeamPlayerNameCVA — keep the transfer in/out color on
  // the description chip too instead of letting the role bg mask it.
  compoundVariants: [
    {
      mode: 'transferIn',
      role: TRANSFER_ROLES,
      className:
        'border-none bg-[--teamplayer-transferIn-desc-bg] text-[--teamplayer-transferIn-desc-text]',
    },
    {
      mode: 'transferOut',
      role: TRANSFER_ROLES,
      className:
        'border-none bg-[--teamplayer-transferOut-desc-bg] text-[--teamplayer-transferOut-desc-text]',
    },
  ],
});

/** In-match events for a single player, shown as icons on the shirt. */
export interface TeamPlayerMatchEvents {
  goals?: number;
  assists?: number;
  ownGoal?: boolean;
  yellowCard?: boolean;
  redCard?: boolean;
  subOff?: boolean;
  penaltyMiss?: boolean;
  penaltySave?: boolean;
}

/**
 * Match events sit in four fixed corner slots, one badge each — goals top-left,
 * assist bottom-left, cards top-right, substitution bottom-right. Slots hold
 * their position whether or not their neighbours are filled.
 */
const EVENT_SLOT = {
  topLeft: 'absolute left-0 top-0',
  bottomLeft: 'absolute left-0 top-[28px]',
  topRight: 'absolute right-0 top-0',
  bottomRight: 'absolute right-0 top-[28px]',
} as const;

export interface TeamPlayerProps extends TeamShirtProps {
  className?: string;
  nameClassName?: string;
  descClassName?: string;
  mode: NonNullable<VariantProps<typeof TeamPlayerCVA>['mode']>;
  role?: VariantProps<typeof TeamPlayerCVA>['role'];
  status?: VariantProps<typeof TeamPlayerCVA>['status'];

  name: string;
  desc: string;
  matchEvents?: TeamPlayerMatchEvents;
  showSubstitutedBadge?: boolean;
  /** Player scored in a gameweek where the manager played the Goal Bonus booster. */
  showGoalBonusBadge?: boolean;
  disableRoleHighlight?: boolean;
  allowPressWhenDisabled?: boolean;
  transferIconDirection?: 'left' | 'right' | 'up' | 'down';
  showResetIcon?: boolean;
  isGameLive?: boolean;
  transferDisabled?: boolean;
  transferDisabledTitle?: string;

  onPress?: (mode: NonNullable<VariantProps<typeof TeamPlayerCVA>['mode']>) => void;
  onResetPress?: () => void;
  onTransferIconPress?: () => void;
}

/** A single goal shows the bare ball; two or more collapse into a counted pill. */
const GoalBadge: FC<{ count: number }> = ({ count }) => {
  const ball = <Goal className="size-[16px] text-[--color-neurtal-1000]" />;
  if (count < 2) return ball;

  return (
    <span className="inline-flex h-[16px] items-center gap-[1px] rounded-full bg-white pr-[4px] shadow-[0_0_0_1px_rgba(34,37,38,0.15)]">
      {ball}
      <span className="text-[9px] font-black leading-none text-[#222526]">{count}</span>
    </span>
  );
};

export const TeamPlayer: FC<TeamPlayerProps> = ({
  className,
  nameClassName,
  descClassName,
  mode,
  status,
  role,
  team,
  name,
  desc,
  matchEvents,
  showSubstitutedBadge = false,
  showGoalBonusBadge = false,
  disableRoleHighlight = false,
  allowPressWhenDisabled = false,
  transferIconDirection,
  showResetIcon = true,
  transferDisabled = false,
  transferDisabledTitle,
  onPress,
  onResetPress,
  onTransferIconPress,
  ...otherProps
}) => {
  const roleForStyle = disableRoleHighlight ? undefined : role;
  const isTransferState = mode === 'transfer' || mode === 'transferIn' || mode === 'transferOut';
  const hasTopLeftBadge = isTransferState || mode === 'substitute' || showSubstitutedBadge;
  const roleBadgeClassName = hasTopLeftBadge
    ? 'absolute top-0 left-[20px]'
    : 'absolute -top-[2px] -left-[2px]';
  const renderResetIcon = useCallback(() => {
    if (!showResetIcon || !onResetPress) {
      return null;
    }

    return (
      <span
        role="button"
        tabIndex={0}
        aria-label="Reset selection"
        className={twMerge(['absolute', 'right-[-2px]', 'top-0', 'size-[16px]', 'rounded-full'])}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          onResetPress();
        }}
        onKeyDown={event => {
          if (event.key !== 'Enter' && event.key !== ' ') {
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          onResetPress();
        }}
      >
        <Reset
          height={undefined}
          width={undefined}
          className={twMerge(['size-[16px]', 'text-[#222526]'])}
        />
      </span>
    );
  }, [onResetPress, showResetIcon]);
  const renderSubstitutedBadge = useCallback(() => {
    if (!showSubstitutedBadge) {
      return null;
    }

    return (
      <div className={twMerge(['absolute', 'left-0', 'top-0', 'size-[16px]', 'overflow-hidden'])}>
        <img src={SUBBED_IN_BADGE_ICON_URL} alt="" className="absolute inset-0 size-full" />
      </div>
    );
  }, [showSubstitutedBadge]);

  const renderMode = useCallback(() => {
    switch (mode) {
      case 'add':
        return (
          <div
            className={twMerge(['absolute', 'flex', 'items-center', 'justify-center'])}
            style={{
              top: 'calc((12/56) * 100%)',
            }}
          >
            <AddPlayer
              height={undefined}
              width={undefined}
              className={twMerge(['h-[16px]', 'w-[16px]', 'text-[--teamplayer-add-icon]'])}
            />
          </div>
        );
      case 'remove':
        return (
          <span
            role="button"
            tabIndex={0}
            aria-label="Reset selection"
            className={twMerge(['absolute', 'flex', 'top-0', 'right-0'])}
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              onResetPress?.();
            }}
            onKeyDown={event => {
              if (event.key !== 'Enter' && event.key !== ' ') {
                return;
              }
              event.preventDefault();
              event.stopPropagation();
              onResetPress?.();
            }}
          >
            <AddPlayer
              height={undefined}
              width={undefined}
              className={twMerge([
                'h-[16px]',
                'w-[16px]',
                'text-[--teamplayer-remove-icon] rotate-45',
              ])}
            />
          </span>
        );
      case 'select':
        return renderResetIcon();
      case 'substitute':
        return (
          <div
            className={twMerge(['absolute', 'left-0', 'top-0', 'size-[16px]', 'overflow-hidden'])}
          >
            <img src={substituteIcon} alt="" className="absolute inset-0 size-full" />
          </div>
        );
      case 'transfer':
        return (
          <span
            role="button"
            tabIndex={transferDisabled ? -1 : 0}
            aria-label="Add player to swap list"
            aria-disabled={transferDisabled}
            title={transferDisabled ? transferDisabledTitle : undefined}
            className={twMerge([
              'absolute',
              'flex',
              'top-0',
              'left-0',
              transferDisabled && 'cursor-not-allowed opacity-50',
            ])}
            onClick={event => {
              if (transferDisabled) return;
              event.preventDefault();
              event.stopPropagation();
              onTransferIconPress?.();
            }}
            onKeyDown={event => {
              if (transferDisabled) return;
              if (event.key !== 'Enter' && event.key !== ' ') {
                return;
              }
              event.preventDefault();
              event.stopPropagation();
              onTransferIconPress?.();
            }}
          >
            <Transfer
              height={undefined}
              width={undefined}
              className={twMerge(['h-[16px]', 'w-[16px]', 'text-[--teamplayer-transfer-icon]'])}
            />
          </span>
        );
      case 'transferIn':
        return (
          <>
            <ArrowLeft className="absolute top-0 left-0 w-[16px] h-[16px] text-[--color-green-200]" />
            {renderResetIcon()}
          </>
        );
      case 'transferOut':
        return (
          <>
            <ArrowLeft className="text-[--color-red-200] rotate-180 absolute top-0 left-0  w-[16px] h-[16px] " />
            {renderResetIcon()}
          </>
        );
      default:
        return null;
    }
  }, [
    mode,
    onResetPress,
    onTransferIconPress,
    renderResetIcon,
    transferIconDirection,
    transferDisabled,
    transferDisabledTitle,
  ]);

  const renderRole = useCallback(() => {
    switch (role) {
      case 'captain':
        return <Captain className={roleBadgeClassName} />;
      case 'pendingCaptain':
        return (
          <span
            className={twMerge(
              roleBadgeClassName,
              'inline-flex size-[16px] items-center justify-center rounded-full border border-dashed border-[#A87400] bg-[#FFF6D8] text-[8px] font-black leading-none text-[#7A5700]'
            )}
          >
            C
          </span>
        );
      case 'maxCaptain':
        return (
          <span
            className={twMerge(
              roleBadgeClassName,
              'inline-flex size-[16px] items-center justify-center rounded-full bg-[#F8CC46] text-[9px] font-black leading-none text-[#231F20] shadow-[0_0_0_1px_rgba(35,31,32,0.18)]'
            )}
          >
            C
          </span>
        );
      case 'viceCaptain':
        return <ViceCaptain className={roleBadgeClassName} />;
      case 'pendingViceCaptain':
        return (
          <span
            className={twMerge(
              roleBadgeClassName,
              'inline-flex size-[16px] items-center justify-center rounded-full border border-dashed border-[#6E4BC3] bg-[#F7F1FF] text-[7px] font-black leading-none text-[#5A3DA6]'
            )}
          >
            VC
          </span>
        );
      default:
        return null;
    }
  }, [role, roleBadgeClassName]);

  const renderStatus = useCallback((): ReactElement | null => {
    switch (status) {
      case 'injured':
      case 'suspended':
      case 'unavailable':
        return <Injured />;
      default:
        return null;
    }
  }, [status]);

  // Bottom-left is shared by the injury/suspension badge, the Goal Bonus badge and
  // an assist, in that order of precedence.
  const renderBottomLeft = useCallback((): ReactElement | null => {
    const badge =
      renderStatus() ??
      (showGoalBonusBadge ? <GoalBonus aria-label="Goal Bonus" className="size-[16px]" /> : null) ??
      (matchEvents?.assists ? (
        <Assist className="size-[16px] text-[--color-neurtal-1000]" />
      ) : null);

    return badge ? <span className={EVENT_SLOT.bottomLeft}>{badge}</span> : null;
  }, [renderStatus, showGoalBonusBadge, matchEvents]);

  const renderMatchEvents = useCallback((): ReactElement | null => {
    if (!matchEvents) {
      return null;
    }

    const goals = matchEvents.goals ?? 0;

    let topLeft: ReactElement | null = null;
    if (goals > 0) {
      topLeft = <GoalBadge count={goals} />;
    } else if (matchEvents.ownGoal) {
      topLeft = <Goal className="size-[16px] text-[--color-red-200]" />;
    } else if (matchEvents.penaltySave) {
      topLeft = <PenaltySave className="size-[16px] text-[--color-blue-200]" />;
    } else if (matchEvents.penaltyMiss) {
      topLeft = <PenaltyMiss className="size-[16px] text-[--color-red-200]" />;
    }

    const topRight = matchEvents.redCard ? (
      <RedTicket className="size-[16px]" />
    ) : matchEvents.yellowCard ? (
      <YellowTicket className="size-[16px]" />
    ) : null;

    const bottomRight = matchEvents.subOff ? (
      <TransferInOut className="size-[16px] rotate-180 text-[--color-red-200]" />
    ) : matchEvents.subOn ? (
      <TransferInOut className="size-[16px] text-[--color-green-200]" />
    ) : null;

    return (
      <>
        {topLeft ? <span className={EVENT_SLOT.topLeft}>{topLeft}</span> : null}
        {topRight ? <span className={EVENT_SLOT.topRight}>{topRight}</span> : null}
        {bottomRight ? <span className={EVENT_SLOT.bottomRight}>{bottomRight}</span> : null}
      </>
    );
  }, [matchEvents]);

  return (
    <button
      className={twMerge('relative flex h-max w-max flex-col items-center', TeamPlayerCVA())}
      onClick={() => {
        onPress?.(mode);
      }}
      disabled={mode === 'disabled' && !allowPressWhenDisabled}
      aria-disabled={mode === 'disabled'}
    >
      <TeamShirt team={team} className={twMerge(className, 'mb-[-20px]')} {...otherProps}>
        {renderRole()}
        {renderBottomLeft()}
        {renderMatchEvents()}
        {mode !== 'substitute' ? renderSubstitutedBadge() : null}
        {renderMode()}
      </TeamShirt>

      <div
        className={twMerge([
          'flex',
          'flex-col',
          'bottom-0',
          'w-full',
          'h-full',
          'text-red-900',
          'text-center',
          'items-center',
        ])}
      >
        <div
          id="team-player-name"
          className={twMerge(
            TeamPlayerNameCVA({ mode, role: roleForStyle, status }),
            'w-[64px] md:w-[88px] h-[18px] flex items-center',
            nameClassName
          )}
        >
          <Typography
            className="w-full overflow-hidden text-ellipsis whitespace-nowrap text-[9px]"
            variant={'paragraph000'}
          >
            {name}
          </Typography>
        </div>
        <div
          className={twMerge(
            TeamPlayerDescCVA({ mode, role: roleForStyle }),
            'w-[64px] md:w-[88px]',
            descClassName
          )}
        >
          <Typography variant={'paragraph000'}>{desc}</Typography>
        </div>
      </div>
    </button>
  );
};
